import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteSendAs } from '../../../../../api/users-api';
import { handleErrors } from '../../../../../utils/errors';
import { SendAsModel } from '../../../../../../types';
import SharedDialog from '../../../../../shared/shared-dialog';
import { useStore } from '../../../../state/zustand-store';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import { Messages } from '../../../../../utils/message-constants';
import ChangeLogDialog from '../../../../../shared/change-log-dialog';
import { useState } from 'react';
import {
  isAbleToSeeUIElement,
  UserRoles,
} from '../../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../../utils/use-user-settings';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
interface DeleteSendAsDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedAccount: SendAsModel;
  sendAsAccounts: SendAsModel[];
}

export default function DeleteSendAsDialog({
  open,
  handleClose,
  userEmail,
  selectedAccount,
  sendAsAccounts,
}: DeleteSendAsDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const { changeLogRoles } = useUserSettings();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteSendAs(userEmail, selectedAccount.sendAsEmail, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Deleted('Send As account'),
      });

      setOpenChangeLogDialog(false);
      setChangeLogMessage('');

      let newList = [];
      newList = sendAsAccounts.filter(
        (account) => account.sendAsEmail !== selectedAccount.sendAsEmail
      );
      const primary = newList.find((account) => account.isPrimary);
      const currentDefault = newList.find((account) => account.isDefault);
      if (!currentDefault) {
        primary!.isDefault = true;
        newList = sendAsAccounts.filter(
          (account) =>
            account.sendAsEmail !== selectedAccount.sendAsEmail &&
            account.sendAsEmail !== primary!.sendAsEmail
        );
        newList.push(primary!);
      } else {
        newList = sendAsAccounts.filter(
          (account) => account.sendAsEmail !== selectedAccount.sendAsEmail
        );
      }

      queryClient.setQueryData([UserKeys.SendAsAccounts, userEmail], newList);

      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting(
          'the selected send as account'
        ),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.gmail_settings
          .delete_send_as_email,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.sendAs,
        }
      );
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, UserRoles.RemoveGmailSendAs.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <>
      <SharedDialog
        open={open}
        handleClose={handleClose}
        title="Delete Send As Account"
        dialogContent={
          <Box>
            <Typography sx={{ mb: 2 }}>
              Delete <strong>{selectedAccount.sendAsEmail}</strong> as a Send As
              account for <strong>{userEmail}</strong>?
            </Typography>
          </Box>
        }
        primaryButton={{
          title: 'Delete Account',
          action: handleSubmit,
          loading: isPending,
          disabled: isPending,
          color: 'error',
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          mutate();
        }}
        mutateLoading={isPending}
      />
    </>
  );
}
